import { action, computed, observable } from 'mobx';
import { version } from 'mobx-sync';
import { isNil, equals } from 'ramda';
import { Gender } from '~client/model/gender';
import { PaymentMeans } from '~client/model/payment-means';
import { UserAuthenticationState } from '~client/model/user-authentication-state';
import { UserProfileResponse } from '~client/model/user-profile-response';
import { datetime } from '~common/helper/datetime';
import { Config } from '~web/config';
import { BaseStore } from '~web/store/base/base-store';
import {
  EMPTY_SHIPPING_ADDRESS,
  ShippingAddress,
} from '~web/view/components/transaction/shipping/shipping-address';

@version(3)
export class UserProfileStore extends BaseStore {
  @observable result: UserProfileResponse;
  @observable shippingAddress: ShippingAddress = EMPTY_SHIPPING_ADDRESS;

  @action
  setResult(result: UserProfileResponse): void {
    this.result = result;
  }

  @action
  setShippingAddress(shippingAddress: ShippingAddress): void {
    this.shippingAddress = shippingAddress;
  }

  @action
  clear(): void {
    this.result = null;
    this.shippingAddress = EMPTY_SHIPPING_ADDRESS;
  }

  @computed
  get id(): number | null {
    return this.result?.id;
  }

  @computed
  get firstName(): string | null {
    return this.result?.firstName;
  }

  @computed
  get lastName(): string | null {
    return this.result?.lastName;
  }

  @computed
  get dateOfBirth(): string | null {
    return this.result?.dateOfBirth;
  }

  @computed
  get age(): number {
    const parsedDob = datetime.parse(this.dateOfBirth, Config.dateTimeFormats.dateOfBirth);
    return Math.floor(datetime.diff(datetime.now(), parsedDob, 'year', true));
  }

  @computed
  get gender(): Gender | null {
    return this.result?.gender;
  }

  @computed
  get countryName(): string | null {
    return this.result?.country;
  }

  @computed
  get countryCode(): string | null {
    return this.result?.countryCode;
  }

  @computed
  get postalCode(): string | null {
    return this.result?.postalCode;
  }

  @computed
  get isEventReady(): boolean | null {
    return this.result?.eventReady;
  }

  @computed
  get isPhotoVerified(): boolean | null {
    return this.result?.profilePictureVerificationStatus;
  }

  @computed
  get isEmailVerified(): boolean {
    if (isNil(this.result)) {
      return false;
    }
    return this.result.userAuthenticationState === UserAuthenticationState.EMAIL_VERIFIED;
  }

  @computed
  get emailAddress(): string | null {
    return this.result?.emailAddress;
  }

  @computed
  get profilePictureVerificationStatus(): boolean | null {
    return this.result?.profilePictureVerificationStatus;
  }

  @computed
  get paymentMeans(): PaymentMeans[] | null {
    return this.result?.paymentMeans;
  }

  @computed
  get defaultPaymentMethod(): PaymentMeans | null {
    return this.paymentMeans?.find((it) => it.defaultMeans);
  }

  @computed
  get hasAddress(): boolean {
    return !equals(this.shippingAddress, EMPTY_SHIPPING_ADDRESS);
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/web/src/js/store/user-profile/user-profile-store.ts